import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import EventoService from '../../services/eventoService';
import NotificacionService from '../../services/notificacionService';
import { cambioEstadoService } from '../../services/cambioEstadoService';
import CrearEventoModal from '../../components/CrearEventoModal';
import ChecklistTramite from '../../components/Tramite/ChecklistTramite';
import BotonCancelarReactivarTramite from '../../components/Tramite/BotonCancelarReactivarTramite';
import TarjetaCargaJauregui from '../../components/Tramite/TarjetaCargaJauregui';
import TarjetaCobroTramite from '../../components/Cobranzas/TarjetaCobroTramite';
import SeccionCobranzaTramite from '../../components/Cobranzas/SeccionCobranzaTramite';
import HistorialTramite from '../../components/HistorialTramite';
import PanelAccionesAvanzadasTramite from '../../components/Administracion/PanelAccionesAvanzadasTramite';
import BannerEstadoFinal from '../../components/BannerEstadoFinal';
import WhatsAppHeaderButton from '../../components/WhatsAppHeaderButton';
import PanelUbicacionTramite from '../../components/PanelUbicacionTramite';
import FeedbackButton from '../../components/FeedbackButton';
import NotaComentario from '../../components/NotaComentario';
import TarjetaTurnoAnses from '../../components/TarjetaTurnoAnses';
import ExpedientesAnsesTramite, { enSeguimientoDeExpediente } from '../../components/ExpedientesAnsesTramite';
import InputMonto from '../../components/InputMonto';
import useCustomModal from '../../hooks/useCustomModal';
import useVolverAtras from '../../hooks/useVolverAtras';
import BotonGenerarCaratula from '../../components/Caratula/BotonGenerarCaratula';
import CustomModal from '../../components/CustomModal';
import { useAuth } from '../../auth/AuthContext';
import { ModalCrearTurno } from '../Recepcion/TurneroPage';
import turneroStyles from '../../style/pages/Turnero.module.css';
import {
    getTramiteReajusteById, actualizarChecklistItem, actualizarTramiteReajuste, actualizarComentariosReajuste,
    actualizarCalculoReajuste, actualizarCobroReajuste, avanzarEstadoReajuste, guardarSentenciaReajuste,
} from '../../services/reajusteService';
import { getPautasReajuste, crearPautaReajuste } from '../../services/pautaReajusteService';
import { subirArchivo, descargarArchivo, obtenerArchivosPorTramite, eliminarArchivo } from '../../services/archivoService';
import { apellidoNombre } from '../../utils/nombrePersona';
import EtiquetaTramite from '../../components/EtiquetaTramite';
import ChipAmparoIniciado from '../../components/Amparo/ChipAmparoIniciado';

// Clase del badge contador (X/Y): verde cuando está completo, gris en caso contrario.
const claseBadgeContador = (completados, total) => (total > 0 && completados === total ? 'bg-success' : 'bg-secondary');

const formatLocalFecha = (fechaInput) => {
    if (!fechaInput) return '';
    if (typeof fechaInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaInput)) return fechaInput;
    const d = new Date(fechaInput);
    if (Number.isNaN(d.getTime())) return '';
    return new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
};

const formatMoneda = (valor) => (valor == null ? null : Number(valor).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 2 }));

const BENEFICIO_LABEL = { JUBILACION: 'Jubilación', PENSION: 'Pensión' };
const TIPO_REAJUSTE_LABEL = { COMUN: 'Común', REPARACION_HISTORICA: 'Reparación Histórica', DOCENTE: 'Docente', OTRO: 'Otro' };
const RESULTADO_150_LABEL = { PENDIENTE: 'Pendiente', FAVORABLE: 'Favorable', DESFAVORABLE: 'Desfavorable' };
const ESTADOS_CON_SENTENCIA = ['Sentencia', 'Ejecución / Cobro'];
const ESTADO_INICIAL_REAJUSTE = 'Cálculo Jauregui y evaluación interna';

// Camino completo del ciclo de Reajuste (réplica del "Progreso del Trámite" de RTI/ART, en
// VistaDetalladaTramite.js). A diferencia de RTI, acá el catálogo es chico: se muestran SIEMPRE
// los 6 pasos, no se arma dinámicamente según hasta dónde llegó el trámite. El último paso es un
// combo que se resuelve según a qué cierre llegó (incluye las salidas que no pasan por todo el
// camino: "Sin diferencia / No inicia" desde el Cálculo, o "Cancelado" desde cualquier lado).
const PASOS_REAJUSTE = [
    'Cálculo Jauregui y evaluación interna',
    'Reclamo administrativo (ANSES)',
    'Instancia judicial',
    'Sentencia',
    'Ejecución / Cobro',
];
const NODO_CIERRE_PENDIENTE = { label: 'Favorable / Desfavorable', fallido: false };
const NODOS_CIERRE_REAJUSTE = {
    'Cerrado favorable': { label: 'Favorable', fallido: false },
    'Cerrado desfavorable': { label: 'Desfavorable', fallido: true },
    'Sin diferencia / No inicia': { label: 'Sin diferencia / No inicia', fallido: true },
    'Cancelado': { label: 'Cancelado', fallido: true },
};

/** Línea de progreso tipo RTI. "Completado" se calcula contra el historial REAL de cambios de
 * estado (no por índice del paso): de "Sentencia" se puede pasar a un cierre sin pasar por
 * "Ejecución / Cobro", y si se pintara por índice ese paso saltado quedaría marcado como
 * visitado sin haberlo estado. */
function ProgresoReajuste({ nombreEstadoActual, historialEstados, onClick }) {
    const visitados = useMemo(
        () => new Set((historialEstados || []).map((h) => h.nombreEstado).filter(Boolean)),
        [historialEstados]
    );
    const cierreAlcanzado = !!NODOS_CIERRE_REAJUSTE[nombreEstadoActual];
    const nodoCierre = NODOS_CIERRE_REAJUSTE[nombreEstadoActual] || NODO_CIERRE_PENDIENTE;

    const nodos = [
        ...PASOS_REAJUSTE.map((nombre) => ({
            key: nombre,
            label: nombre,
            actual: nombreEstadoActual === nombre,
            completado: visitados.has(nombre) && nombreEstadoActual !== nombre,
            fallido: false,
        })),
        {
            key: '__cierre__',
            label: nodoCierre.label,
            actual: cierreAlcanzado,
            completado: false,
            fallido: nodoCierre.fallido,
        },
    ];

    return (
        <div
            className="card shadow-sm border-0 rounded-4 p-4 mb-3"
            style={{ backgroundColor: '#f8f9fa', cursor: 'pointer', transition: 'all 0.2s ease' }}
            onClick={onClick}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e9ecef'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold text-secondary mb-0">Progreso del Trámite</h5>
                <span className="text-primary"><i className="bi bi-arrow-right-circle me-1"></i>Ver detalles</span>
            </div>
            <div className="d-flex justify-content-between align-items-center position-relative" style={{ overflowX: 'auto', padding: '10px 0' }}>
                <div className="position-absolute border-top" style={{ top: '35%', left: '3%', width: '94%', borderTop: '3px solid #dee2e6', zIndex: 1 }}></div>
                {nodos.map((nodo) => {
                    let color = '#adb5bd'; // Gris (Pendiente)
                    if (nodo.completado) color = '#0d6efd'; // Azul (Completado)
                    if (nodo.actual) color = nodo.fallido ? '#dc3545' : '#198754'; // Rojo (Fallido/cierre negativo) o Verde (Actual)
                    const borde = nodo.actual ? `4px solid ${color}` : `2px solid ${color}`;
                    return (
                        <div key={nodo.key} className="d-flex flex-column align-items-center text-center" style={{ position: 'relative', zIndex: 2, minWidth: '140px' }}>
                            <div className="rounded-circle mb-2" style={{ width: '22px', height: '22px', backgroundColor: color, border: borde, transition: 'all 0.3s ease' }}></div>
                            <span style={{ fontSize: '0.82rem', color: nodo.actual ? color : (nodo.completado ? '#0d6efd' : '#6c757d'), fontWeight: nodo.actual ? '600' : 'normal', whiteSpace: 'nowrap' }}>
                                {nodo.label}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


/** yyyy-MM-dd → dd/mm/yyyy, sin pasar por Date (evita el corrimiento de huso horario). */
function formatearFechaCorta(fechaISO) {
    if (!fechaISO) return '';
    const [y, m, d] = fechaISO.split('-');
    return `${d}/${m}/${y}`;
}


/** Panel destacado del cálculo de reajuste (diferencia/retroactivo, con y sin IPC). Estético y
 * llamativo, estilo Panel de Aportes de Plan de Pago, pero con datos reales (cálculo 100%
 * manual). Se edita/carga desde acá mismo (botón arriba a la derecha), independiente de "Editar
 * datos" de Datos del Beneficio. Retroactivo adelantado/pendiente/pagado NO van acá: viven en su
 * propio panel (PanelCobroReajuste), editable mientras el trámite está en "Ejecución / Cobro". */
function PanelDestacadoReajuste({ tramite, onGuardarCalculo }) {
    const [editMode, setEditMode] = useState(false);
    const [form, setForm] = useState({});
    const [guardando, setGuardando] = useState(false);

    const abrirEdicion = () => {
        setForm({
            diferenciaConIpc: tramite.diferenciaConIpc ?? '',
            diferenciaSinIpc: tramite.diferenciaSinIpc ?? '',
            retroactivoConIpc: tramite.retroactivoConIpc ?? '',
            retroactivoSinIpc: tramite.retroactivoSinIpc ?? '',
        });
        setEditMode(true);
    };

    const guardar = async () => {
        setGuardando(true);
        try {
            await onGuardarCalculo({
                diferenciaConIpc: form.diferenciaConIpc !== '' ? form.diferenciaConIpc : null,
                diferenciaSinIpc: form.diferenciaSinIpc !== '' ? form.diferenciaSinIpc : null,
                retroactivoConIpc: form.retroactivoConIpc !== '' ? form.retroactivoConIpc : null,
                retroactivoSinIpc: form.retroactivoSinIpc !== '' ? form.retroactivoSinIpc : null,
            });
            setEditMode(false);
        } finally {
            setGuardando(false);
        }
    };

    if (editMode) {
        const campoMoneda = (name, label, colClass = 'col-md-6') => (
            <div className={colClass}>
                <label className="form-label fw-semibold">{label}</label>
                <InputMonto value={form[name]} onChange={(v) => setForm(p => ({ ...p, [name]: v }))} />
            </div>
        );
        return (
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold text-primary mb-0">
                                <i className="bi bi-graph-up-arrow me-2"></i>Cálculo del Reajuste
                            </h4>
                            <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle rounded-pill px-3 py-2">
                                <i className="bi bi-pencil me-1"></i>Editando
                            </span>
                        </div>
                        <div className="p-4 border rounded-3 bg-light">
                            <div className="row g-3 mb-1">
                                {campoMoneda('diferenciaConIpc', 'Diferencia CON IPC')}
                                {campoMoneda('diferenciaSinIpc', 'Diferencia SIN IPC')}
                            </div>
                            <div className="row g-3 mt-1">
                                {campoMoneda('retroactivoConIpc', 'Retroactivo con IPC')}
                                {campoMoneda('retroactivoSinIpc', 'Retroactivo sin IPC')}
                            </div>
                            <div className="d-flex justify-content-center gap-3 mt-4">
                                <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold" onClick={guardar} disabled={guardando}>
                                    {guardando ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle-fill"></i>Guardar cálculo</>}
                                </button>
                                <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold" onClick={() => setEditMode(false)} disabled={guardando}>
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const conIpc = formatMoneda(tramite.diferenciaConIpc);
    const sinIpc = formatMoneda(tramite.diferenciaSinIpc);
    const retroactivoConIpc = formatMoneda(tramite.retroactivoConIpc);
    const retroactivoSinIpc = formatMoneda(tramite.retroactivoSinIpc);
    const sinDatos = conIpc == null && sinIpc == null && retroactivoConIpc == null && retroactivoSinIpc == null;

    const tile = (titulo, valor, gradiente, icono) => (
        <div className="col-md-3">
            <div className="card shadow-sm h-100" style={{ border: 'none', borderRadius: '12px', background: gradiente }}>
                <div className="card-body" style={{ padding: '1.25rem' }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <div style={{ color: 'white' }}>
                            <div style={{ fontSize: '0.78rem', opacity: 0.9, marginBottom: '0.3rem', fontWeight: 600 }}>{titulo}</div>
                            <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>{valor ?? '—'}</div>
                        </div>
                        <i className={`bi ${icono}`} style={{ fontSize: '2.25rem', color: 'rgba(255,255,255,0.35)' }}></i>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="row g-4 mt-1">
            <div className="col-12">
                <div className="card shadow-lg border-0 rounded-4 p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="fw-bold text-primary mb-0">
                            <i className="bi bi-graph-up-arrow me-2"></i>Cálculo del Reajuste
                        </h4>
                        <button className="btn btn-outline-primary btn-sm rounded-pill px-3 fw-semibold" onClick={abrirEdicion}>
                            <i className="bi bi-pencil me-2"></i>{sinDatos ? 'Cargar cálculo' : 'Editar cálculo'}
                        </button>
                    </div>
                    <div className="row g-3">
                        {tile('DIFERENCIA CON IPC', conIpc, 'var(--success-solid)', 'bi-graph-up')}
                        {tile('DIFERENCIA SIN IPC', sinIpc, 'var(--primary)', 'bi-graph-up')}
                        {tile('RETROACTIVO CON IPC', retroactivoConIpc, 'var(--gradient-purple)', 'bi-cash-stack')}
                        {tile('RETROACTIVO SIN IPC', retroactivoSinIpc, 'var(--violet)', 'bi-cash-stack')}
                    </div>
                    {sinDatos && (
                        <div className="alert alert-secondary border-0 rounded-3 mt-3 mb-0 text-center">
                            <i className="bi bi-hourglass-split me-2"></i>Pendiente de carga del cálculo manual
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * Panel de "Ejecución / Cobro": retroactivo adelantado, pendiente y pagado (con su fecha). A
 * diferencia del cálculo (que es la referencia de lo que corresponde), esto es lo que ANSES fue
 * efectivamente resolviendo mientras se espera que cierre el expediente "150": puede reajustar el
 * haber mensual o pagar un adelanto varias veces, así que se edita libremente (no es un dato de
 * una sola carga en el cambio de estado, a diferencia de una primera versión de esta pantalla).
 * "Retroactivo pendiente" es lo que sobró por un mal pago (pagar fuera de término, etc.) — se
 * carga a mano, no se calcula solo.
 *
 * Habilitado ya desde "Sentencia" (ver mostrarPanelCobro/ESTADOS_CON_SENTENCIA) y no recién en
 * "Ejecución / Cobro": entre que la sentencia (1ª instancia o cámara, la que quede firme) y se
 * abre el "150", ANSES puede empezar a reajustar el haber o pagar un adelanto sin que el trámite
 * haya avanzado todavía de estado.
 */
function PanelCobroReajuste({ tramite, onGuardarCobro }) {
    const [editMode, setEditMode] = useState(false);
    const [form, setForm] = useState({});
    const [guardando, setGuardando] = useState(false);

    const abrirEdicion = () => {
        setForm({
            adelanto: tramite.adelanto ?? '',
            retroactivoPendiente: tramite.retroactivoPendiente ?? '',
            montoCobrado: tramite.montoCobrado ?? '',
            fechaCobro: tramite.fechaCobro || '',
        });
        setEditMode(true);
    };

    const guardar = async () => {
        setGuardando(true);
        try {
            await onGuardarCobro({
                adelanto: form.adelanto !== '' ? form.adelanto : null,
                retroactivoPendiente: form.retroactivoPendiente !== '' ? form.retroactivoPendiente : null,
                montoCobrado: form.montoCobrado !== '' ? form.montoCobrado : null,
                fechaCobro: form.fechaCobro || null,
            });
            setEditMode(false);
        } finally {
            setGuardando(false);
        }
    };

    if (editMode) {
        const campoMoneda = (name, label, colClass = 'col-md-6') => (
            <div className={colClass}>
                <label className="form-label fw-semibold">{label}</label>
                <InputMonto value={form[name]} onChange={(v) => setForm(p => ({ ...p, [name]: v }))} />
            </div>
        );
        return (
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold text-primary mb-0">
                                <i className="bi bi-cash-coin me-2"></i>Ejecución / Cobro
                            </h4>
                            <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle rounded-pill px-3 py-2">
                                <i className="bi bi-pencil me-1"></i>Editando
                            </span>
                        </div>
                        <div className="p-4 border rounded-3 bg-light">
                            <div className="row g-3 mb-1">
                                {campoMoneda('adelanto', 'Retroactivo adelantado')}
                                {campoMoneda('retroactivoPendiente', 'Retroactivo pendiente')}
                            </div>
                            <div className="row g-3 mt-1">
                                {campoMoneda('montoCobrado', 'Retroactivo pagado')}
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">Fecha de pago</label>
                                    <input type="date" className="form-control shadow-sm" value={form.fechaCobro}
                                        onChange={(e) => setForm(p => ({ ...p, fechaCobro: e.target.value }))} />
                                </div>
                            </div>
                            <div className="d-flex justify-content-center gap-3 mt-4">
                                <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold" onClick={guardar} disabled={guardando}>
                                    {guardando ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle-fill"></i>Guardar</>}
                                </button>
                                <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold" onClick={() => setEditMode(false)} disabled={guardando}>
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const adelantado = formatMoneda(tramite.adelanto);
    const pendiente = formatMoneda(tramite.retroactivoPendiente);
    const pagado = formatMoneda(tramite.montoCobrado);
    const sinDatos = adelantado == null && pendiente == null && pagado == null && !tramite.fechaCobro;

    const tile = (titulo, valor, gradiente, icono) => (
        <div className="col-md-4">
            <div className="card shadow-sm h-100" style={{ border: 'none', borderRadius: '12px', background: gradiente }}>
                <div className="card-body" style={{ padding: '1.25rem' }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <div style={{ color: 'white' }}>
                            <div style={{ fontSize: '0.78rem', opacity: 0.9, marginBottom: '0.3rem', fontWeight: 600 }}>{titulo}</div>
                            <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>{valor ?? '—'}</div>
                        </div>
                        <i className={`bi ${icono}`} style={{ fontSize: '2.25rem', color: 'rgba(255,255,255,0.35)' }}></i>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="row g-4 mt-1">
            <div className="col-12">
                <div className="card shadow-lg border-0 rounded-4 p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="fw-bold text-primary mb-0">
                            <i className="bi bi-cash-coin me-2"></i>Ejecución / Cobro
                        </h4>
                        <button className="btn btn-outline-primary btn-sm rounded-pill px-3 fw-semibold" onClick={abrirEdicion}>
                            <i className="bi bi-pencil me-2"></i>{sinDatos ? 'Cargar cobro' : 'Editar cobro'}
                        </button>
                    </div>
                    <div className="row g-3">
                        {tile('RETROACTIVO ADELANTADO', adelantado, 'var(--text-muted)', 'bi-wallet2')}
                        {tile('RETROACTIVO PENDIENTE', pendiente, 'var(--gradient-warning)', 'bi-hourglass-split')}
                        {tile('RETROACTIVO PAGADO', pagado, 'var(--success-solid)', 'bi-cash-stack')}
                    </div>
                    {tramite.fechaCobro && (
                        <div className="mt-3 text-muted small">
                            <i className="bi bi-calendar-check me-1"></i>Fecha de pago: <strong>{tramite.fechaCobro}</strong>
                        </div>
                    )}
                    {sinDatos && (
                        <div className="alert alert-secondary border-0 rounded-3 mt-3 mb-0 text-center">
                            <i className="bi bi-hourglass-split me-2"></i>Todavía no hay datos de cobro cargados
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/** Sección de una instancia de sentencia (1ª instancia o cámara): texto libre + checklist
 * de pautas/fallos aplicados (catálogo de Datos Maestros, con nota opcional por pauta). */
/** Checklist de pautas/fallos de un tipo (FALLO o INDICE), estilo list-group como el resto de
 * checklists del sistema, con una fila final "Otro" para crear un dato maestro nuevo sin salir
 * de la ficha (se suma al catálogo compartido de Datos Maestros al instante). */
function ListaPautas({ tipo, titulo, icon, idTramite, tipoInstancia, pautasDisponibles, pautasSeleccionadas, onToggle, onNota, onPautaCreada }) {
    const [agregandoOtro, setAgregandoOtro] = useState(false);
    const [nombreNuevo, setNombreNuevo] = useState('');
    const [creando, setCreando] = useState(false);

    const items = pautasDisponibles.filter(p => p.tipo === tipo);

    const confirmarNuevo = async () => {
        const nombre = nombreNuevo.trim();
        if (!nombre) return;
        setCreando(true);
        try {
            const nueva = await crearPautaReajuste({ nombre, tipo, activo: true });
            onPautaCreada(nueva);
            onToggle(nueva.id);
            setNombreNuevo('');
            setAgregandoOtro(false);
            toast.success(`"${nombre}" agregado al catálogo`);
        } catch (err) {
            console.error('Error al crear pauta:', err);
            toast.error('No se pudo crear el dato maestro');
        } finally {
            setCreando(false);
        }
    };

    return (
        <div className="mb-3">
            <h6 className="fw-bold text-secondary mb-2"><i className={`bi ${icon} me-2`}></i>{titulo}</h6>
            <ul className="list-group list-group-flush border rounded-3 overflow-hidden">
                {items.map(p => (
                    <li key={p.id} className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="form-check mb-0">
                            <input className="form-check-input" type="checkbox" id={`pauta-${tipoInstancia}-${p.id}`}
                                checked={!!pautasSeleccionadas[p.id]?.checked} onChange={() => onToggle(p.id)} />
                            <label className="form-check-label" htmlFor={`pauta-${tipoInstancia}-${p.id}`}>{p.nombre}</label>
                        </div>
                        {pautasSeleccionadas[p.id]?.checked && (
                            <input className="form-control form-control-sm" style={{ maxWidth: 220 }} placeholder="Nota (opcional)"
                                value={pautasSeleccionadas[p.id]?.nota || ''} onChange={(e) => onNota(p.id, e.target.value)} />
                        )}
                    </li>
                ))}
                <li className="list-group-item">
                    {agregandoOtro ? (
                        <div className="input-group input-group-sm">
                            <span className="input-group-text bg-light"><i className="bi bi-plus-lg"></i></span>
                            <input type="text" className="form-control" autoFocus placeholder={`Nombre del nuevo ${tipo === 'FALLO' ? 'fallo' : 'índice/pauta'}...`}
                                value={nombreNuevo} onChange={(e) => setNombreNuevo(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmarNuevo(); } }} />
                            <button className="btn btn-primary" type="button" onClick={confirmarNuevo} disabled={creando || !nombreNuevo.trim()}>
                                {creando ? <span className="spinner-border spinner-border-sm" role="status"></span> : <i className="bi bi-check-lg"></i>}
                            </button>
                            <button className="btn btn-outline-secondary" type="button" onClick={() => { setAgregandoOtro(false); setNombreNuevo(''); }} disabled={creando}>
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                    ) : (
                        <button type="button" className="btn btn-sm btn-link text-decoration-none p-0 d-flex align-items-center gap-1" onClick={() => setAgregandoOtro(true)}>
                            <i className="bi bi-plus-circle"></i>Otro (crear en Datos Maestros)
                        </button>
                    )}
                </li>
            </ul>
        </div>
    );
}

function SeccionInstanciaSentencia({ idTramite, tipoInstancia, titulo, dataInicial, pautasDisponibles, onGuardado, onPautaCreada }) {
    const esCamara = tipoInstancia === 'CAMARA';
    const [form, setForm] = useState({
        link: '', queOrdenaPbu: '', observacionesPbu: '', queOrdenaPcPap: '', observacionesPcPap: '',
        confirma: '', modifica: '', revoca: '', observacionesExtras: '',
    });
    const [pautasSeleccionadas, setPautasSeleccionadas] = useState({}); // idPauta -> { checked, nota }
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        const d = dataInicial || {};
        setForm({
            link: d.link || '', queOrdenaPbu: d.queOrdenaPbu || '', observacionesPbu: d.observacionesPbu || '',
            queOrdenaPcPap: d.queOrdenaPcPap || '', observacionesPcPap: d.observacionesPcPap || '',
            confirma: d.confirma || '', modifica: d.modifica || '', revoca: d.revoca || '',
            observacionesExtras: d.observacionesExtras || '',
        });
        const mapa = {};
        (d.pautas || []).forEach(p => { mapa[p.idPauta] = { checked: true, nota: p.nota || '' }; });
        setPautasSeleccionadas(mapa);
    }, [dataInicial]);

    const cargada = !!dataInicial;

    const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const togglePauta = (idPauta) => {
        setPautasSeleccionadas(prev => {
            const actual = prev[idPauta];
            if (actual?.checked) {
                return { ...prev, [idPauta]: { checked: false, nota: actual.nota } };
            }
            return { ...prev, [idPauta]: { checked: true, nota: actual?.nota || '' } };
        });
    };
    const cambiarNotaPauta = (idPauta, nota) => {
        setPautasSeleccionadas(prev => ({ ...prev, [idPauta]: { ...(prev[idPauta] || { checked: true }), nota } }));
    };

    const guardar = async () => {
        setGuardando(true);
        try {
            const pautas = Object.entries(pautasSeleccionadas)
                .filter(([, v]) => v.checked)
                .map(([idPauta, v]) => ({ idPauta: Number(idPauta), nota: v.nota || '' }));
            const dto = { ...form, pautas };
            const guardada = await guardarSentenciaReajuste(idTramite, tipoInstancia, dto);
            onGuardado(guardada);
            toast.success(`Sentencia (${titulo}) guardada correctamente`);
        } catch (err) {
            console.error('Error al guardar sentencia:', err);
            toast.error('No se pudo guardar la sentencia');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div className="card shadow-sm border-0 rounded-4 p-4 h-100" style={{ backgroundColor: '#fff' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold text-primary mb-0"><i className="bi bi-bank2 me-2"></i>{titulo}</h5>
                <span className={`badge rounded-pill ${cargada ? 'bg-success' : 'bg-secondary'}`}>
                    {cargada ? 'Cargada' : 'Sin cargar'}
                </span>
            </div>

            <div className="mb-3">
                <label className="form-label fw-semibold">Link</label>
                <div className="input-group">
                    <span className="input-group-text bg-light"><i className="bi bi-link-45deg"></i></span>
                    <input className="form-control shadow-sm" name="link" value={form.link} onChange={handleChange} />
                </div>
            </div>

            <h6 className="fw-bold text-secondary mb-2"><i className="bi bi-file-text me-2"></i>PBU</h6>
            <div className="row g-2 mb-3">
                <div className="col-md-6">
                    <label className="form-label small fw-semibold">Qué ordena</label>
                    <textarea className="form-control shadow-sm" rows={2} name="queOrdenaPbu" value={form.queOrdenaPbu} onChange={handleChange} />
                </div>
                <div className="col-md-6">
                    <label className="form-label small fw-semibold">Observaciones</label>
                    <textarea className="form-control shadow-sm" rows={2} name="observacionesPbu" value={form.observacionesPbu} onChange={handleChange} />
                </div>
            </div>

            <h6 className="fw-bold text-secondary mb-2"><i className="bi bi-file-text me-2"></i>PC / PAP</h6>
            <div className="row g-2 mb-3">
                <div className="col-md-6">
                    <label className="form-label small fw-semibold">Qué ordena</label>
                    <textarea className="form-control shadow-sm" rows={2} name="queOrdenaPcPap" value={form.queOrdenaPcPap} onChange={handleChange} />
                </div>
                <div className="col-md-6">
                    <label className="form-label small fw-semibold">Observaciones</label>
                    <textarea className="form-control shadow-sm" rows={2} name="observacionesPcPap" value={form.observacionesPcPap} onChange={handleChange} />
                </div>
            </div>

            {esCamara && (
                <>
                    <h6 className="fw-bold text-secondary mb-2"><i className="bi bi-bank me-2"></i>Cámara</h6>
                    <div className="row g-2 mb-3">
                        <div className="col-md-4">
                            <label className="form-label small fw-semibold">Confirma</label>
                            <textarea className="form-control shadow-sm" rows={2} name="confirma" value={form.confirma} onChange={handleChange} />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label small fw-semibold">Modifica</label>
                            <textarea className="form-control shadow-sm" rows={2} name="modifica" value={form.modifica} onChange={handleChange} />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label small fw-semibold">Revoca</label>
                            <textarea className="form-control shadow-sm" rows={2} name="revoca" value={form.revoca} onChange={handleChange} />
                        </div>
                    </div>
                </>
            )}

            <div className="mb-3">
                <label className="form-label small fw-semibold">Observaciones extras</label>
                <textarea className="form-control shadow-sm" rows={2} name="observacionesExtras" value={form.observacionesExtras} onChange={handleChange} />
            </div>

            <hr className="my-2" />

            <ListaPautas tipo="FALLO" titulo="Fallos aplicados" icon="bi-hammer" idTramite={idTramite} tipoInstancia={tipoInstancia}
                pautasDisponibles={pautasDisponibles} pautasSeleccionadas={pautasSeleccionadas}
                onToggle={togglePauta} onNota={cambiarNotaPauta} onPautaCreada={onPautaCreada} />

            <ListaPautas tipo="INDICE" titulo="Índices / pautas aplicados" icon="bi-graph-up" idTramite={idTramite} tipoInstancia={tipoInstancia}
                pautasDisponibles={pautasDisponibles} pautasSeleccionadas={pautasSeleccionadas}
                onToggle={togglePauta} onNota={cambiarNotaPauta} onPautaCreada={onPautaCreada} />

            <div className="d-flex justify-content-center mt-2">
                <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold" onClick={guardar} disabled={guardando}>
                    {guardando
                        ? <span className="spinner-border spinner-border-sm" role="status"></span>
                        : cargada
                            ? <><i className="bi bi-pencil-fill"></i>Modificar sentencia</>
                            : <><i className="bi bi-check-circle-fill"></i>Guardar sentencia</>}
                </button>
            </div>
        </div>
    );
}

// Mini-formulario por transición (patrón RTI/ART): cada avance de estado carga su propio
// dato de hito. Todos los campos son opcionales — solo agilizan cargar el dato del hito en
// el momento del cambio; se pueden completar después editando la ficha.
const TRANSICIONES_BASE = {
    'Cálculo Jauregui y evaluación interna': [
        // No hay botón para avanzar a "Reclamo administrativo (ANSES)": a ese estado se llega
        // sacando el turno en ANSES, que es lo que de verdad inicia el reclamo, y de eso se
        // encarga la tarjeta de acá abajo. Tener las dos cosas hacía que el botón pidiera una
        // fecha de turno que la tarjeta ya maneja, y dejaba trámites avanzados de estado sin
        // ningún turno cargado.
        {
            destino: 'SIN_DIFERENCIA',
            label: 'Cerrar: Sin diferencia / No inicia',
            icon: 'bi-x-octagon',
            variant: 'outline-secondary',
            campos: [
                { name: 'motivoCierre', label: 'Motivo (por qué no conviene iniciar)', type: 'textarea' },
            ],
        },
    ],
    'Reclamo administrativo (ANSES)': [
        {
            destino: 'INSTANCIA_JUDICIAL',
            label: 'Avanzar a Instancia Judicial',
            icon: 'bi-arrow-right-circle-fill',
            variant: 'primary',
            campos: [
                { name: 'fechaDenegatoria', label: 'Fecha de denegatoria de ANSES', type: 'date', icon: 'bi-calendar-x' },
                { name: 'fechaInicioJuicio', label: 'Fecha de inicio de juicio', type: 'date', icon: 'bi-calendar-plus' },
                { name: 'juzgado', label: 'Juzgado', type: 'text', icon: 'bi-bank' },
                { name: 'caratula', label: 'Carátula', type: 'text', icon: 'bi-file-earmark-text' },
                { name: 'nroExpedienteJudicial', label: 'N° de expediente judicial', type: 'text', icon: 'bi-hash' },
            ],
        },
    ],
    'Instancia judicial': [
        {
            destino: 'SENTENCIA',
            label: 'Avanzar a Sentencia',
            icon: 'bi-arrow-right-circle-fill',
            variant: 'primary',
            campos: [
                { name: 'fechaSentencia', label: 'Fecha de sentencia (opcional)', type: 'date', icon: 'bi-calendar-event' },
            ],
        },
    ],
    'Sentencia': [
        {
            destino: 'EJECUCION_COBRO',
            label: 'Avanzar a Ejecución / Cobro',
            icon: 'bi-arrow-right-circle-fill',
            variant: 'primary',
            campos: [
                { name: 'tiene150', label: 'Se abrió el expediente "150"', type: 'checkbox' },
                {
                    name: 'resultado150', label: 'Resultado del 150', type: 'select', soloSi: 'tiene150',
                    opciones: [
                        { value: 'PENDIENTE', label: 'Pendiente' },
                        { value: 'FAVORABLE', label: 'Favorable' },
                        { value: 'DESFAVORABLE', label: 'Desfavorable' },
                    ],
                },
                // Retroactivo adelantado/pendiente/pagado NO se cargan acá: se editan aparte, en
                // el panel de "Ejecución / Cobro" (mientras se espera que ANSES resuelva el "150"
                // puede reajustar el haber o pagar un adelanto varias veces, no es de una sola vez).
            ],
        },
        // El reajuste puede cerrarse ya en Sentencia (ej: sentencia desfavorable y no se
        // ejecuta nada) sin pasar por Ejecución / Cobro.
        {
            destino: 'CERRADO_FAVORABLE',
            label: 'Cerrar: favorable',
            icon: 'bi-check-circle-fill',
            variant: 'success',
            campos: [
                { name: 'motivoCierre', label: 'Detalle del cierre (opcional)', type: 'textarea' },
            ],
        },
        {
            destino: 'CERRADO_DESFAVORABLE',
            label: 'Cerrar: desfavorable',
            icon: 'bi-x-octagon-fill',
            variant: 'danger',
            campos: [
                { name: 'motivoCierre', label: 'Motivo del cierre desfavorable', type: 'textarea' },
            ],
        },
    ],
    // Etapa final del flujo de avance: hasta ahora no tenía entrada en el mapa y el trámite
    // quedaba sin forma de cerrarse una vez cobrado.
    'Ejecución / Cobro': [
        {
            destino: 'CERRADO_FAVORABLE',
            label: 'Cerrar: favorable',
            icon: 'bi-check-circle-fill',
            variant: 'success',
            campos: [
                { name: 'motivoCierre', label: 'Detalle del cierre (opcional)', type: 'textarea' },
            ],
        },
        {
            destino: 'CERRADO_DESFAVORABLE',
            label: 'Cerrar: desfavorable',
            icon: 'bi-x-octagon-fill',
            variant: 'danger',
            campos: [
                { name: 'motivoCierre', label: 'Motivo del cierre desfavorable', type: 'textarea' },
            ],
        },
    ],
};

// Cancelar / dar de baja el trámite (V208). Va aparte del mapa de arriba porque está disponible
// desde CUALQUIER estado vivo, no en un punto puntual del ciclo: un reajuste se puede caer en
// cualquier momento (el cliente desiste, se detecta que estaba mal cargado).
//
// No confundir con "Sin diferencia / No inicia": ese cierre significa que se evaluó el caso y no
// convenía iniciarlo, que es un resultado del análisis. Cancelado significa que el trámite no
// siguió. Mezclarlos ensucia el reporte.
const TRANSICION_CANCELAR = {
    destino: 'CANCELADO',
    label: 'Cancelar trámite',
    icon: 'bi-slash-circle-fill',
    variant: 'outline-secondary',
    campos: [
        { name: 'comentario', label: 'Motivo de la cancelación', type: 'textarea' },
    ],
};

// Desde Cancelado se vuelve al principio del ciclo, igual que la reactivación que RTI ya tenía y
// que el resto de los módulos tiene desde V208.
const TRANSICION_REACTIVAR = {
    destino: 'CALCULO_INICIAL',
    label: 'Reactivar trámite',
    icon: 'bi-arrow-counterclockwise',
    variant: 'outline-primary',
    campos: [
        { name: 'comentario', label: 'Motivo de la reactivación', type: 'textarea' },
    ],
};

const TRANSICIONES_POR_ESTADO = {
    ...Object.fromEntries(
        Object.entries(TRANSICIONES_BASE).map(([estado, transiciones]) => [
            estado,
            [...transiciones, TRANSICION_CANCELAR],
        ])
    ),
    'Cancelado': [TRANSICION_REACTIVAR],
};

function MiniFormularioTransicion({ idTramite, transicion, onGuardado }) {
    const [abierto, setAbierto] = useState(false);
    const [valores, setValores] = useState({});
    const [guardando, setGuardando] = useState(false);

    const handleChange = (name, value) => setValores(prev => ({ ...prev, [name]: value }));

    const guardar = async () => {
        setGuardando(true);
        try {
            const payload = { destino: transicion.destino, ...valores };
            const actualizado = await avanzarEstadoReajuste(idTramite, payload);
            onGuardado(actualizado);
            toast.success('Estado actualizado correctamente');
            setAbierto(false);
            setValores({});
        } catch (err) {
            console.error('Error al avanzar de estado:', err);
            toast.error('No se pudo avanzar de estado');
        } finally {
            setGuardando(false);
        }
    };

    if (!abierto) {
        return (
            <button className={`btn btn-${transicion.variant || 'primary'} d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold`} onClick={() => setAbierto(true)}>
                <i className={`bi ${transicion.icon || 'bi-arrow-right-circle'}`}></i>{transicion.label}
            </button>
        );
    }

    return (
        <div className="p-4 border rounded-3 bg-light w-100">
            <h5 className="fw-semibold text-primary mb-3">
                <i className={`bi ${transicion.icon || 'bi-arrow-right-circle'} me-2`}></i>{transicion.label}
            </h5>
            <div className="row g-3">
                {transicion.campos.map(campo => {
                    if (campo.soloSi && !valores[campo.soloSi]) return null;
                    return (
                        <div key={campo.name} className={campo.type === 'textarea' ? 'col-12' : 'col-md-6'}>
                            <label className="form-label fw-semibold">{campo.label}</label>
                            {campo.type === 'textarea' ? (
                                <textarea className="form-control shadow-sm" rows={3}
                                    value={valores[campo.name] || ''} onChange={(e) => handleChange(campo.name, e.target.value)} />
                            ) : campo.type === 'checkbox' ? (
                                <div className="form-check ps-0">
                                    <div className="form-check form-switch">
                                        <input className="form-check-input" type="checkbox" role="switch" id={`campo-${campo.name}`}
                                            checked={!!valores[campo.name]} onChange={(e) => handleChange(campo.name, e.target.checked)} />
                                        <label className="form-check-label" htmlFor={`campo-${campo.name}`}>
                                            {valores[campo.name] ? 'Sí' : 'No'}
                                        </label>
                                    </div>
                                </div>
                            ) : campo.type === 'select' ? (
                                <select className="form-select shadow-sm" value={valores[campo.name] || ''}
                                    onChange={(e) => handleChange(campo.name, e.target.value)}>
                                    <option value="">—</option>
                                    {campo.opciones.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            ) : (
                                <div className="input-group">
                                    {campo.icon && <span className="input-group-text bg-light"><i className={`bi ${campo.icon}`}></i></span>}
                                    <input type={campo.type} className="form-control shadow-sm"
                                        value={valores[campo.name] || ''} onChange={(e) => handleChange(campo.name, e.target.value)} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="d-flex justify-content-center gap-3 mt-4">
                <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold" onClick={guardar} disabled={guardando}>
                    {guardando ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle-fill"></i>Confirmar</>}
                </button>
                <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                    onClick={() => { setAbierto(false); setValores({}); }} disabled={guardando}>
                    Cancelar
                </button>
            </div>
        </div>
    );
}

/** Reemplaza el selector libre del historial como forma primaria de cambiar de estado
 * (patrón RTI/ART: cada estado dispara el evento/mini-formulario que aporta su dato, no un
 * simple dropdown vacío). El historial completo sigue disponible desde el botón "Ver historial". */
function SeccionAvanzarEstado({ idTramite, nombreEstadoActual, onAvanzado }) {
    const transiciones = TRANSICIONES_POR_ESTADO[nombreEstadoActual] || [];
    if (transiciones.length === 0) {
        return (
            <div className="alert alert-secondary border-0 rounded-3 mb-0 d-flex align-items-center gap-2">
                <i className="bi bi-flag-fill"></i>Este trámite está en una etapa final del ciclo de estados.
            </div>
        );
    }
    return (
        <div className="d-flex flex-wrap gap-3">
            {transiciones.map(t => (
                <MiniFormularioTransicion key={t.destino} idTramite={idTramite} transicion={t} onGuardado={onAvanzado} />
            ))}
        </div>
    );
}

function VistaDetalladaTramiteReajuste() {
    const { idTramite } = useParams();
    const navigate = useNavigate();
    const volverAtras = useVolverAtras('/tramites');
    const { user } = useAuth();
    const { modalState, hideModal, showModal } = useCustomModal();

    const [tramite, setTramite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pautasDisponibles, setPautasDisponibles] = useState([]);
    // Historial real de cambios de estado: alimenta el "Progreso del Trámite" (qué pasos se
    // visitaron de verdad, para no pintar como completado un paso que el trámite saltó).
    const [historialEstados, setHistorialEstados] = useState([]);

    // Edición de datos del trámite (no el cliente)
    const [editMode, setEditMode] = useState(false);
    const [tramiteEdit, setTramiteEdit] = useState(null);
    const [guardandoDatos, setGuardandoDatos] = useState(false);

    // Herramienta: crear turno prellenado con los datos del cliente del trámite
    const [mostrarModalTurno, setMostrarModalTurno] = useState(false);

    const [eventos, setEventos] = useState([]);
    const [cargandoEventos, setCargandoEventos] = useState(true);
    const [mostrarModalEvento, setMostrarModalEvento] = useState(false);
    const [fechaSeleccionadaEvento, setFechaSeleccionadaEvento] = useState(new Date());

    const [modalHistorialAbierto, setModalHistorialAbierto] = useState(false);
    const [subiendoItem, setSubiendoItem] = useState(null);

    // Archivos del trámite (fuente única: el checklist enlaza por idArchivo; "otros" son los no vinculados)
    const [archivos, setArchivos] = useState([]);
    const [subiendoGeneral, setSubiendoGeneral] = useState(false);

    const recargarTramite = useCallback(async () => {
        try {
            const data = await getTramiteReajusteById(idTramite);
            setTramite(data);
        } catch (error) {
            console.error('Error al cargar trámite de Reajuste:', error);
        }
    }, [idTramite]);

    const recargarArchivos = useCallback(async () => {
        try {
            const data = await obtenerArchivosPorTramite(idTramite);
            setArchivos(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error al cargar archivos:', error);
        }
    }, [idTramite]);

    const recargarEventos = useCallback(async () => {
        try {
            const response = await EventoService.getEventosPorTramite(idTramite);
            setEventos(response.data || []);
        } catch (error) {
            console.error('Error recargando eventos:', error);
        }
    }, [idTramite]);

    const recargarHistorialEstados = useCallback(async () => {
        try {
            const data = await cambioEstadoService.getHistorialEstados(idTramite);
            setHistorialEstados(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error al cargar historial de estados:', error);
        }
    }, [idTramite]);

    // Cualquier movimiento del turno ANSES puede tocar el trámite: registrar el ingreso lo mueve
    // a "Reclamo administrativo (ANSES)" y una resolución lo cierra. Se releen los tres (el
    // historial de estados incluye el nuevo cambio, para que "Progreso del Trámite" se actualice).
    const refrescarPorTurnoAnses = useCallback(async () => {
        await Promise.all([recargarTramite(), recargarEventos(), recargarHistorialEstados()]);
    }, [recargarTramite, recargarEventos, recargarHistorialEstados]);

    useEffect(() => {
        const cargar = async () => {
            setLoading(true);
            setCargandoEventos(true);
            try {
                const [tramiteResult, eventosResult, archivosResult, pautasResult, historialResult] = await Promise.allSettled([
                    getTramiteReajusteById(idTramite),
                    EventoService.getEventosPorTramite(idTramite),
                    obtenerArchivosPorTramite(idTramite),
                    getPautasReajuste(true),
                    cambioEstadoService.getHistorialEstados(idTramite),
                ]);
                setTramite(tramiteResult.status === 'fulfilled' ? tramiteResult.value : null);
                setEventos(eventosResult.status === 'fulfilled' ? (eventosResult.value.data || []) : []);
                setArchivos(archivosResult.status === 'fulfilled' && Array.isArray(archivosResult.value) ? archivosResult.value : []);
                setPautasDisponibles(pautasResult.status === 'fulfilled' && Array.isArray(pautasResult.value) ? pautasResult.value : []);
                setHistorialEstados(historialResult.status === 'fulfilled' && Array.isArray(historialResult.value) ? historialResult.value : []);
            } finally {
                setLoading(false);
                setCargandoEventos(false);
            }
        };
        cargar();
    }, [idTramite]);

    // Auto-refresco de eventos (paridad con ART/RTI/Jubilación/PUAM/Plan de Pago)
    useEffect(() => {
        const handleEventoCreado = () => recargarEventos();
        window.addEventListener('eventoCreadoDesdeChat', handleEventoCreado);
        const interval = setInterval(() => recargarEventos(), 30000);
        return () => {
            window.removeEventListener('eventoCreadoDesdeChat', handleEventoCreado);
            clearInterval(interval);
        };
    }, [recargarEventos]);

    const handleGuardarEvento = async (evento) => {
        try {
            const responseEvento = await EventoService.crearEvento({
                ...evento,
                fecha: formatLocalFecha(evento.fecha),
                idTramite: Number(idTramite),
            });
            const idEventoCreado = responseEvento.data.idEvento || responseEvento.data;
            if (evento.tiemposRecordatorio && evento.tiemposRecordatorio.length > 0) {
                await NotificacionService.programarNotificacionesAutomaticas(idEventoCreado, evento.tiemposRecordatorio);
            }
            window.dispatchEvent(new CustomEvent('eventoCreadoDesdeChat', { detail: { timestamp: Date.now(), source: 'tramite-reajuste' } }));
            await recargarEventos();
            setMostrarModalEvento(false);
            toast.success('Evento creado correctamente');
        } catch (error) {
            console.error('Error al crear evento:', error);
            toast.error('Error al crear el evento');
        }
    };

    const formatearFechaEvento = (fechaString) => {
        if (!fechaString) return '';
        const [y, m, d] = fechaString.split('T')[0].split('-');
        return `${d}/${m}/${y}`;
    };

    // ---- Descarga con nombre real (extensión + tipo correctos) ----
    const descargarPorId = async (idArchivo) => {
        try {
            const meta = archivos.find(a => a.idArchivo === idArchivo);
            const nombre = meta?.nombre || `archivo_${idArchivo}`;
            const resp = await descargarArchivo(idArchivo);
            const blob = meta?.tipo ? new Blob([resp.data], { type: meta.tipo }) : new Blob([resp.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = nombre;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error al descargar archivo:', err);
            toast.error('No se pudo descargar el archivo');
        }
    };

    // ---- Checklist ----
    const togglePresentado = async (item) => {
        if (item.idArchivo) return; // con archivo adjunto, el ítem queda marcado y bloqueado
        try {
            await actualizarChecklistItem(item.id, { presentado: !item.presentado, idArchivo: null });
            recargarTramite();
        } catch (err) {
            console.error('Error al actualizar checklist:', err);
        }
    };
    const adjuntarArchivo = async (item, file) => {
        if (!file) return;
        setSubiendoItem(item.id);
        try {
            const resp = await subirArchivo(idTramite, file);
            const idArchivo = resp?.idArchivo ?? null;
            await actualizarChecklistItem(item.id, { presentado: true, idArchivo });
            await Promise.all([recargarTramite(), recargarArchivos()]);
        } catch (err) {
            console.error('Error al adjuntar archivo:', err);
            toast.error('No se pudo adjuntar el archivo');
        } finally {
            setSubiendoItem(null);
        }
    };
    const quitarAdjunto = (item) => {
        showModal({
            title: 'Eliminar documento',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: `¿Seguro que querés eliminar el archivo adjunto de "${item.nombre}"?`,
            onConfirm: async () => {
                try {
                    await eliminarArchivo(item.idArchivo);
                    await actualizarChecklistItem(item.id, { presentado: false, idArchivo: null });
                    await Promise.all([recargarTramite(), recargarArchivos()]);
                    toast.info('Documento eliminado');
                } catch (err) {
                    console.error('Error al eliminar documento:', err);
                    toast.error('No se pudo eliminar el documento');
                }
            },
        });
    };

    // ---- Otros documentos (no vinculados al checklist) ----
    const subirGeneral = async (file) => {
        if (!file) return;
        setSubiendoGeneral(true);
        try {
            await subirArchivo(idTramite, file);
            await recargarArchivos();
            toast.success('Archivo subido');
        } catch (err) {
            console.error('Error al subir archivo:', err);
            toast.error('No se pudo subir el archivo');
        } finally {
            setSubiendoGeneral(false);
        }
    };
    const eliminarGeneral = (idArchivo) => {
        showModal({
            title: 'Eliminar archivo',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: '¿Seguro que querés eliminar este archivo? Esta acción no se puede deshacer.',
            onConfirm: async () => {
                try {
                    await eliminarArchivo(idArchivo);
                    await recargarArchivos();
                    toast.info('Archivo eliminado');
                } catch (err) {
                    console.error('Error al eliminar archivo:', err);
                    toast.error('No se pudo eliminar el archivo');
                }
            },
        });
    };

    // ---- Comentarios (nota editable con firma, igual que ART/RTI/Jubilación/PUAM/Plan de Pago) ----
    const handleGuardarComentario = async (nuevoComentario) => {
        try {
            await actualizarComentariosReajuste(idTramite, nuevoComentario);
            await recargarTramite();
            toast.success('Comentario actualizado correctamente');
        } catch (error) {
            console.error('Error al guardar comentario:', error);
            toast.error('No se pudo guardar el comentario');
            throw error;
        }
    };

    // ---- Editar datos del trámite ----
    // tipoReajuste es el único campo que puede alterar el checklist → puede requerir
    // confirmación de regeneración (mismo patrón que Jubilación/Plan de Pago).
    const handleModificar = () => {
        setTramiteEdit({
            organismo: tramite.organismo || '',
            beneficio: tramite.beneficio || 'JUBILACION',
            tipoReajuste: tramite.tipoReajuste || 'COMUN',
            fechaAdquisicion: tramite.fechaAdquisicion || '',
            fechaReclamoAdministrativo: tramite.fechaReclamoAdministrativo || '',
            nroExpediente: tramite.nroExpediente || '',
            causanteApellidoNombre: tramite.causanteApellidoNombre || '',
            causanteDniCuil: tramite.causanteDniCuil || '',
            causanteFechaNacimiento: tramite.causanteFechaNacimiento || '',
            causanteFechaDefuncion: tramite.causanteFechaDefuncion || '',
            causanteDomicilio: tramite.causanteDomicilio || '',
            causanteRelacionDependencia: tramite.causanteRelacionDependencia || '',
            causanteAutonomoMonotributo: tramite.causanteAutonomoMonotributo || '',
            causanteCuentaAfip: tramite.causanteCuentaAfip || '',
            pbuReajustado: !!tramite.fechaAjustePbu,
            fechaAjustePbu: tramite.fechaAjustePbu || '',
            expedienteConseguido: !!tramite.expedienteConseguido,
            expedienteDigitalizado: !!tramite.expedienteDigitalizado,
            tiene150: !!tramite.tiene150,
            resultado150: tramite.resultado150 || '',
            etiqueta: tramite.etiqueta || '',
        });
        setEditMode(true);
    };
    const handleGuardarDatos = async (confirmarRegenerarChecklist = false) => {
        setGuardandoDatos(true);
        try {
            await actualizarTramiteReajuste(idTramite, {
                ...tramiteEdit,
                organismo: tramiteEdit.organismo || null,
                fechaAdquisicion: tramiteEdit.fechaAdquisicion || null,
                fechaReclamoAdministrativo: tramiteEdit.fechaReclamoAdministrativo || null,
                fechaAjustePbu: tramiteEdit.pbuReajustado ? (tramiteEdit.fechaAjustePbu || null) : null,
                resultado150: tramiteEdit.tiene150 ? (tramiteEdit.resultado150 || null) : null,
                confirmarRegenerarChecklist,
            });
            await recargarTramite();
            setEditMode(false);
            toast.success('Datos del trámite actualizados');
        } catch (error) {
            if (error.response?.status === 409 && error.response?.data?.requiereConfirmacion) {
                const items = error.response.data.items || [];
                showModal({
                    title: 'Confirmar cambios en el checklist',
                    type: 'warning',
                    showCancel: true,
                    confirmText: 'Sí, continuar',
                    cancelText: 'Cancelar',
                    confirmVariant: 'warning',
                    message: (
                        <div>
                            <p>Con estos cambios, los siguientes formularios del checklist dejan de requerirse, pero ya tienen un archivo adjunto:</p>
                            <ul className="mb-2">
                                {items.map((it) => (
                                    <li key={it.id}>
                                        <strong>{it.nombre}</strong> — archivo: {it.nombreArchivo || `#${it.idArchivo}`}
                                    </li>
                                ))}
                            </ul>
                            <p className="mb-0">El archivo no se elimina: va a quedar disponible en "Otros documentos". ¿Confirmás continuar?</p>
                        </div>
                    ),
                    onConfirm: () => handleGuardarDatos(true),
                });
            } else {
                console.error('Error al guardar datos:', error);
                toast.error(error.response?.data || 'No se pudieron guardar los datos');
            }
        } finally {
            setGuardandoDatos(false);
        }
    };

    // ---- Editar cálculo (independiente de "Datos del Beneficio", botón propio en el panel) ----
    const handleGuardarCalculo = async (valores) => {
        try {
            await actualizarCalculoReajuste(idTramite, valores);
            await recargarTramite();
            toast.success('Cálculo actualizado correctamente');
        } catch (error) {
            console.error('Error al guardar el cálculo:', error);
            toast.error('No se pudo guardar el cálculo');
            throw error;
        }
    };

    // ---- Editar panel de Ejecución / Cobro (retroactivo adelantado/pendiente/pagado) ----
    const handleGuardarCobro = async (valores) => {
        try {
            await actualizarCobroReajuste(idTramite, valores);
            await recargarTramite();
            toast.success('Datos de cobro actualizados correctamente');
        } catch (error) {
            console.error('Error al guardar el cobro:', error);
            toast.error('No se pudo guardar el cobro');
            throw error;
        }
    };

    // ---- Avanzar de estado (mini-formulario por transición, patrón RTI/ART) ----
    const handleAvanzarEstado = (tramiteActualizado) => {
        setTramite(tramiteActualizado);
        recargarHistorialEstados();
    };

    // Nueva pauta/fallo creada "al vuelo" desde la sección de Sentencia (opción "Otro"): se
    // suma al catálogo compartido para que ambas instancias (1ª instancia y Cámara) la vean.
    const handlePautaCreada = (nuevaPauta) => {
        setPautasDisponibles(prev => [...prev, nuevaPauta]);
    };

    const handleGuardadaSentencia = (nueva) => {
        setTramite(prev => {
            if (!prev) return prev;
            const sentencias = (prev.sentencias || []).filter(s => s.tipoInstancia !== nueva.tipoInstancia);
            sentencias.push(nueva);
            return { ...prev, sentencias };
        });
    };

    const verDetalleCliente = (cuil) => navigate(`/clientes/vista-detallada/${cuil.toString()}`);

    const sentenciaPrimeraInstancia = useMemo(() => (tramite?.sentencias || []).find(s => s.tipoInstancia === 'PRIMERA_INSTANCIA'), [tramite]);
    const sentenciaCamara = useMemo(() => (tramite?.sentencias || []).find(s => s.tipoInstancia === 'CAMARA'), [tramite]);
    const mostrarSentencia = tramite && (ESTADOS_CON_SENTENCIA.includes(tramite.nombreEstadoActual) || (tramite.sentencias || []).length > 0);
    // Retroactivo adelantado/pendiente/pagado (montoCobrado) y su fecha viven en su propio panel
    // (PanelCobroReajuste), no acá: son datos que se reajustan varias veces, no un hito fijo.
    const tieneHitos = tramite && [
        tramite.fechaLlamado, tramite.fechaTurnoAnses, tramite.fechaPresentacionReclamo, tramite.fechaDenegatoria, tramite.fechaInicioJuicio,
        tramite.juzgado, tramite.caratula, tramite.nroExpedienteJudicial, tramite.fechaSentencia,
    ].some((v) => v != null && v !== '');
    // Habilitado ya desde "Sentencia" y no recién en "Ejecución / Cobro": entre que la sentencia
    // queda firme y se abre el "150", ANSES puede reajustar el haber mensual o pagar un adelanto
    // de retroactivo sin que el trámite haya avanzado todavía de estado — no es una espera vacía.
    const mostrarPanelCobro = tramite && (
        ESTADOS_CON_SENTENCIA.includes(tramite.nombreEstadoActual)
        || [tramite.adelanto, tramite.retroactivoPendiente, tramite.fechaCobro, tramite.montoCobrado].some((v) => v != null && v !== '')
    );

    if (loading) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}></div>
                <p className="mt-3 fw-semibold" style={{ color: '#6B7280' }}>Cargando trámite...</p>
            </div>
        );
    }
    if (!tramite) {
        return <div className="alert alert-danger mt-4">No se encontró el trámite.</div>;
    }

    const cliente = tramite.cliente || {};
    const formularios = tramite.formularios || [];
    const idsEnChecklist = new Set(formularios.map(f => f.idArchivo).filter(Boolean));
    const archivosOtros = archivos.filter(a => !idsEnChecklist.has(a.idArchivo));
    const nombrePorId = archivos.reduce((acc, a) => { acc[a.idArchivo] = a.nombre; return acc; }, {});
    const formatTamano = (bytes) => (bytes == null ? '' : (bytes / (1024 * 1024)).toFixed(2) + ' MB');

    return (
        <div className="container mt-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div style={{ flex: '1 1 20rem', minWidth: 0 }}>
                    <div className="d-flex align-items-center flex-wrap gap-3 mb-1">
                        <h2 className="fw-bold text-primary mb-0">Detalles del Trámite de Reajuste de Haberes</h2>
                        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle rounded-pill fs-6 px-3">
                            REAJUSTE
                        </span>
                        <EtiquetaTramite etiqueta={tramite.etiqueta} />
                        {/* Si sobre este expediente se promovio un amparo por mora ante ANSES, avisa
                            y linkea a su ficha (V239). Es lo que el Excel anota a mano como
                            "AMPARO INICIADO" sobre la fila del tramite. */}
                        <ChipAmparoIniciado idTramite={tramite.idTramite} />
                    </div>
                    {cliente?.apellido && cliente?.nombre && (
                        <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                            <h5 className="text-secondary fw-semibold mb-0">
                                <PersonSearchIcon fontSize="small" className="me-1 mb-1" />
                                {apellidoNombre(cliente.apellido, cliente.nombre)}
                            </h5>
                            {cliente.telefonoPropio && (
                                <WhatsAppHeaderButton
                                    phone={cliente.telefonoPropio}
                                    clientName={`${cliente.nombre} ${cliente.apellido}`}
                                    onClick={() => window.dispatchEvent(
                                        new CustomEvent('openAdminSidebar', { detail: { tab: 'whatsapp' } })
                                    )}
                                />
                            )}
                        </div>
                    )}
                    <PanelUbicacionTramite tramite={tramite} onUpdated={setTramite} />
                </div>
                <div className="d-flex gap-2 mt-1 align-items-center flex-wrap">
                    <BotonGenerarCaratula
                        tipo="REAJUSTE"
                        idTramite={idTramite}
                        onGenerado={recargarArchivos}
                    />
                    <button
                        className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill"
                        onClick={() => setMostrarModalTurno(true)}
                    >
                        <i className="bi bi-calendar-plus"></i> Crear turno
                    </button>
                    <button
                        className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill"
                        onClick={volverAtras}
                    >
                        <ArrowBackIcon fontSize="small" /> Volver
                    </button>
                </div>
            </div>

            <BannerEstadoFinal
                idTramite={tramite.idTramite}
                nombreEstadoActual={tramite.nombreEstadoActual}
            />

            {/* Progreso del Trámite: réplica de la línea de tiempo de RTI/ART, pero con el
                camino completo siempre visible (acá el ciclo tiene pocos pasos). */}
            <ProgresoReajuste
                nombreEstadoActual={tramite.nombreEstadoActual}
                historialEstados={historialEstados}
                onClick={() => setModalHistorialAbierto(true)}
            />

            {/* Estado del ciclo: cada avance dispara el mini-formulario de esa etapa (patrón RTI/ART) */}
            <div className="card shadow-sm border-0 rounded-4 p-4 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div className="d-flex align-items-center gap-3">
                        <h5 className="fw-bold text-secondary mb-0">Estado</h5>
                        <span className="badge bg-success bg-opacity-10 text-success border border-success-subtle rounded-pill fs-6 px-3">
                            {tramite.nombreEstadoActual || 'Cálculo Jauregui y evaluación interna'}
                        </span>
                        {tramite.fechaEstadoActual && (
                            <span className="text-muted small">
                                {new Date(tramite.fechaEstadoActual).toLocaleDateString('es-ES')}
                            </span>
                        )}
                    </div>
                    <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={() => setModalHistorialAbierto(true)}>
                        <i className="bi bi-clock-history me-1"></i>Ver historial
                    </button>
                </div>
                <SeccionAvanzarEstado idTramite={idTramite} nombreEstadoActual={tramite.nombreEstadoActual} onAvanzado={handleAvanzarEstado} />

                {/* El turno ANSES vive acá adentro y no como una tarjeta suelta más abajo: sacar
                    el turno ES el avance a "Reclamo administrativo (ANSES)", y registrar cómo
                    salió la presentación es lo que mueve el trámite después. Separados parecían
                    dos cosas que no se hablan, y quien avanzaba el estado no veía el turno. */}
                <div className="border-top mt-4 pt-3">
                    <h6 className="fw-bold text-secondary mb-3">
                        <i className="bi bi-building me-2"></i>Turno en ANSES
                    </h6>
                    <TarjetaTurnoAnses
                        idTramite={tramite.idTramite}
                        nombreArea="este reajuste"
                        nombreEstadoActual={tramite.nombreEstadoActual}
                        onCambio={refrescarPorTurnoAnses}
                        onEstadoCambiado={refrescarPorTurnoAnses}
                        denegatoriaContinuaEnJuicio
                        pideEscribania
                    />
                    {enSeguimientoDeExpediente(tramite.nombreEstadoActual) && <ExpedientesAnsesTramite idTramite={tramite.idTramite} />}
                </div>
            </div>

            {/* Carga en Jauregui: el trabajo previo al reclamo (pase, plazo, quién carga,
                resultado) MÁS el aviso de llamado al cliente, que antes era una tarjeta aparte al
                pie de la card de Estado. Los dos plazos juntos y calculados por el backend, así la
                ficha y el listado "Para cargar Jauregui" no pueden mostrar cosas distintas.

                Va como card HERMANA de la de Estado y no adentro: metida ahí se leía como una
                sección más del ciclo de estados, y es un trabajo aparte. Y va JUSTO DESPUÉS del
                estado y antes de los comentarios, en el mismo lugar que ocupa en Jubilación, RTI y
                Reconocimiento: es la misma tarjeta, así que tiene que estar siempre donde el ojo
                ya la busca.

                Solo importa ANTES de sacar el turno en ANSES: una vez que se avanzó de estado,
                esta etapa terminó. */}
            {tramite.nombreEstadoActual === ESTADO_INICIAL_REAJUSTE && (
                <TarjetaCargaJauregui idTramite={tramite.idTramite} onCambio={() => recargarTramite()} />
            )}

            {/* Nota editable de comentarios (con firma, igual que ART/RTI/Jubilación/PUAM/Plan de Pago) */}
            <NotaComentario
                valor={tramite.comentarios || ''}
                onGuardar={handleGuardarComentario}
                label="Comentarios del Trámite de Reajuste"
                color="blue"
                nombreUsuario={user?.nombre || ''}
            />

            {/* Cobranza del trámite: desplegable, debajo del estado y los comentarios */}
            <SeccionCobranzaTramite tramite={tramite} />

            {/* Eventos del Trámite */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold text-primary mb-0">Eventos del Trámite</h4>
                            <button
                                className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill fw-semibold"
                                onClick={() => {
                                    const hoy = new Date();
                                    setFechaSeleccionadaEvento(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()));
                                    setMostrarModalEvento(true);
                                }}
                            >
                                <span style={{ fontSize: '1.2rem' }}>+</span> Crear Evento
                            </button>
                        </div>
                        {cargandoEventos ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status"></div>
                                <p className="mt-2 text-muted">Cargando eventos...</p>
                            </div>
                        ) : eventos.length === 0 ? (
                            <div className="alert alert-info border-0 rounded-3 text-center">
                                <p className="mb-0">No hay eventos registrados para este trámite</p>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {eventos.map(evento => (
                                    <div key={evento.idEvento} className="col-md-6 col-lg-4">
                                        <div className="card h-100 border rounded-3 p-3 shadow-sm">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h6 className="fw-bold text-primary mb-0">{evento.titulo}</h6>
                                                {evento.tipoEvento && <span className="badge bg-primary rounded-pill">{evento.tipoEvento}</span>}
                                            </div>
                                            {evento.descripcion && <p className="text-muted small mb-2">{evento.descripcion}</p>}
                                            <div className="mt-auto">
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <small className="text-muted">📅</small>
                                                    <small className="fw-semibold">{formatearFechaEvento(evento.fecha)}</small>
                                                </div>
                                                {evento.todoElDia ? (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">🕐</small>
                                                        <small className="text-success fw-semibold">Todo el día</small>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">🕐</small>
                                                        <small>{evento.horaInicio} - {evento.horaFin}</small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Datos del Beneficio/Expediente + Info del Cliente */}
            <div className="row g-4">
                <div className="col-lg-6">
                    <div className="card shadow-lg border-0 rounded-4 p-4 h-100">
                        <h4 className="fw-bold text-primary mb-3">
                            <i className="bi bi-file-earmark-text me-2"></i>
                            Datos del Beneficio
                        </h4>
                        {!editMode ? (
                            <>
                                <p><strong>Organismo:</strong> {tramite.organismo === 'ANSES' ? 'ANSES' : tramite.organismo === 'PROVINCIA' ? 'Provincia' : 'Sin especificar'}</p>
                                <p><strong>Beneficio:</strong> {BENEFICIO_LABEL[tramite.beneficio] || tramite.beneficio}</p>
                                <p><strong>Tipo de reajuste:</strong> {TIPO_REAJUSTE_LABEL[tramite.tipoReajuste] || tramite.tipoReajuste}</p>
                                <p><strong>Fecha de adquisición:</strong> {tramite.fechaAdquisicion || 'N/A'}</p>
                                <p><strong>Fecha de reclamo administrativo:</strong> {tramite.fechaReclamoAdministrativo || 'N/A'}</p>
                                <p><strong>N° de expediente del beneficio:</strong> {tramite.nroExpediente || 'N/A'}</p>
                                {tramite.beneficio === 'PENSION' && (
                                    <>
                                        <hr />
                                        <h6 className="fw-bold text-primary"><i className="bi bi-person-x me-2"></i>Datos del Causante</h6>
                                        <p><strong>Apellido y Nombre:</strong> {tramite.causanteApellidoNombre || 'N/A'}</p>
                                        <p><strong>DNI/CUIL:</strong> {tramite.causanteDniCuil || 'N/A'}</p>
                                        <p><strong>Fecha de nacimiento:</strong> {tramite.causanteFechaNacimiento || 'N/A'}</p>
                                        <p><strong>Fecha de defunción:</strong> {tramite.causanteFechaDefuncion || 'N/A'}</p>
                                        <p><strong>Domicilio:</strong> {tramite.causanteDomicilio || 'N/A'}</p>
                                        <p><strong>Relación de dependencia:</strong> {tramite.causanteRelacionDependencia || 'N/A'}</p>
                                        <p><strong>Autónomo o monotributo:</strong> {tramite.causanteAutonomoMonotributo === 'AUTONOMO' ? 'Autónomo' : tramite.causanteAutonomoMonotributo === 'MONOTRIBUTO' ? 'Monotributo' : 'N/A'}</p>
                                        <p><strong>Cuenta AFIP:</strong> {tramite.causanteCuentaAfip || 'N/A'}</p>
                                        <hr />
                                    </>
                                )}
                                <p><strong>Expediente conseguido:</strong> {tramite.expedienteConseguido ? 'Sí' : 'No'}</p>
                                <p><strong>El expediente está digitalizado:</strong> {tramite.expedienteDigitalizado ? 'Sí' : 'No'}</p>
                                <p><strong>PBU reajustado:</strong> {tramite.fechaAjustePbu ? `Sí (desde ${tramite.fechaAjustePbu})` : 'No'}</p>
                                <p><strong>Expediente "150":</strong> {tramite.tiene150 ? `Sí — ${RESULTADO_150_LABEL[tramite.resultado150] || 'Pendiente'}` : 'No'}</p>
                                {tramite.etiqueta && <p><strong>Etiqueta:</strong> {tramite.etiqueta}</p>}
                                <ChecklistTramite tramite={tramite} onActualizado={recargarTramite} />
                                <TarjetaCobroTramite
                                    tramite={tramite}
                                    onPactoGenerado={() => Promise.all([recargarTramite(), recargarArchivos()])}
                                />
                                <div className="mt-3 d-flex gap-2 flex-wrap">
                                    <button className="btn btn-outline-primary rounded-pill px-4 fw-semibold" onClick={handleModificar}>
                                        <i className="bi bi-pencil me-2"></i>Editar datos
                                    </button>
                                    <BotonCancelarReactivarTramite tramite={tramite} onActualizado={recargarTramite} />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Organismo</label>
                                    <input className="form-control" value="ANSES" disabled readOnly
                                        style={{ color: '#000', opacity: 1 }} />
                                </div>
                                <div className="row g-2 mb-2">
                                    <div className="col-6">
                                        <label className="form-label fw-semibold">Beneficio</label>
                                        <select className="form-select" value={tramiteEdit.beneficio}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, beneficio: e.target.value }))}>
                                            <option value="JUBILACION">Jubilación</option>
                                            <option value="PENSION">Pensión</option>
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label fw-semibold">Tipo de reajuste</label>
                                        <select className="form-select" value={tramiteEdit.tipoReajuste}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, tipoReajuste: e.target.value }))}>
                                            <option value="COMUN">Común</option>
                                            <option value="REPARACION_HISTORICA">Reparación Histórica</option>
                                            <option value="DOCENTE">Docente</option>
                                            <option value="OTRO">Otro</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="row g-2 mb-2">
                                    <div className="col-6">
                                        <label className="form-label fw-semibold">Fecha de adquisición</label>
                                        <input type="date" className="form-control" value={tramiteEdit.fechaAdquisicion || ''}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, fechaAdquisicion: e.target.value }))} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label fw-semibold">Fecha de reclamo administrativo</label>
                                        <input type="date" className="form-control" value={tramiteEdit.fechaReclamoAdministrativo || ''}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, fechaReclamoAdministrativo: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <label className="form-label fw-semibold">N° de expediente del beneficio</label>
                                    <input type="text" className="form-control" value={tramiteEdit.nroExpediente || ''}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, nroExpediente: e.target.value }))} />
                                </div>
                                {tramiteEdit.beneficio === 'PENSION' && (
                                    <>
                                        <hr />
                                        <h6 className="fw-bold text-primary"><i className="bi bi-person-x me-2"></i>Datos del Causante</h6>
                                        <div className="mb-2">
                                            <label className="form-label fw-semibold">Apellido y Nombre del causante</label>
                                            <input type="text" className="form-control" value={tramiteEdit.causanteApellidoNombre || ''}
                                                onChange={(e) => setTramiteEdit(p => ({ ...p, causanteApellidoNombre: e.target.value }))} />
                                        </div>
                                        <div className="mb-2">
                                            <label className="form-label fw-semibold">DNI/CUIL del causante</label>
                                            <input type="text" className="form-control" value={tramiteEdit.causanteDniCuil || ''}
                                                onChange={(e) => setTramiteEdit(p => ({ ...p, causanteDniCuil: e.target.value }))} />
                                        </div>
                                        <div className="row g-2 mb-2">
                                            <div className="col-6">
                                                <label className="form-label fw-semibold">Fecha de nacimiento</label>
                                                <input type="date" className="form-control" value={tramiteEdit.causanteFechaNacimiento || ''}
                                                    onChange={(e) => setTramiteEdit(p => ({ ...p, causanteFechaNacimiento: e.target.value }))} />
                                            </div>
                                            <div className="col-6">
                                                <label className="form-label fw-semibold">Fecha de defunción</label>
                                                <input type="date" className="form-control" value={tramiteEdit.causanteFechaDefuncion || ''}
                                                    onChange={(e) => setTramiteEdit(p => ({ ...p, causanteFechaDefuncion: e.target.value }))} />
                                            </div>
                                        </div>
                                        <div className="mb-2">
                                            <label className="form-label fw-semibold">Domicilio del causante</label>
                                            <input type="text" className="form-control" value={tramiteEdit.causanteDomicilio || ''}
                                                onChange={(e) => setTramiteEdit(p => ({ ...p, causanteDomicilio: e.target.value }))} />
                                            <small className="text-muted">Tal como figura en el acta de defunción.</small>
                                        </div>
                                        <div className="mb-2">
                                            <label className="form-label fw-semibold">Relación de dependencia</label>
                                            <input type="text" className="form-control" value={tramiteEdit.causanteRelacionDependencia || ''}
                                                onChange={(e) => setTramiteEdit(p => ({ ...p, causanteRelacionDependencia: e.target.value }))} />
                                            <small className="text-muted">Por ejemplo: "4 años y 10 meses".</small>
                                        </div>
                                        <div className="mb-2">
                                            <label className="form-label fw-semibold">Autónomo o monotributo</label>
                                            <select className="form-select" value={tramiteEdit.causanteAutonomoMonotributo || ''}
                                                onChange={(e) => setTramiteEdit(p => ({ ...p, causanteAutonomoMonotributo: e.target.value }))}>
                                                <option value="">Sin especificar</option>
                                                <option value="AUTONOMO">Autónomo</option>
                                                <option value="MONOTRIBUTO">Monotributo</option>
                                            </select>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Cuenta AFIP</label>
                                            <input type="text" className="form-control" value={tramiteEdit.causanteCuentaAfip || ''}
                                                onChange={(e) => setTramiteEdit(p => ({ ...p, causanteCuentaAfip: e.target.value }))} />
                                        </div>
                                        <hr />
                                    </>
                                )}
                                <div className="form-check mb-2">
                                    <input className="form-check-input" type="checkbox" id="edit-expte-conseguido"
                                        checked={!!tramiteEdit.expedienteConseguido}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, expedienteConseguido: e.target.checked }))} />
                                    <label className="form-check-label" htmlFor="edit-expte-conseguido">Expediente conseguido</label>
                                </div>
                                <div className="form-check mb-2">
                                    <input className="form-check-input" type="checkbox" id="edit-expte-digitalizado"
                                        checked={!!tramiteEdit.expedienteDigitalizado}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, expedienteDigitalizado: e.target.checked }))} />
                                    <label className="form-check-label" htmlFor="edit-expte-digitalizado">El expediente está digitalizado</label>
                                </div>
                                <div className="form-check mb-2">
                                    <input className="form-check-input" type="checkbox" id="edit-pbu-reajustado"
                                        checked={!!tramiteEdit.pbuReajustado}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, pbuReajustado: e.target.checked }))} />
                                    <label className="form-check-label" htmlFor="edit-pbu-reajustado">PBU reajustado</label>
                                </div>
                                {tramiteEdit.pbuReajustado && (
                                    <div className="mb-2">
                                        <label className="form-label fw-semibold">Fecha de ajuste del PBU</label>
                                        <input type="date" className="form-control" value={tramiteEdit.fechaAjustePbu || ''}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, fechaAjustePbu: e.target.value }))} />
                                    </div>
                                )}
                                <div className="form-check mb-2">
                                    <input className="form-check-input" type="checkbox" id="edit-tiene-150"
                                        checked={!!tramiteEdit.tiene150}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, tiene150: e.target.checked }))} />
                                    <label className="form-check-label" htmlFor="edit-tiene-150">Expediente "150" abierto</label>
                                </div>
                                {tramiteEdit.tiene150 && (
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Resultado del 150</label>
                                        <select className="form-select" value={tramiteEdit.resultado150 || ''}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, resultado150: e.target.value }))}>
                                            <option value="PENDIENTE">Pendiente</option>
                                            <option value="FAVORABLE">Favorable</option>
                                            <option value="DESFAVORABLE">Desfavorable</option>
                                        </select>
                                    </div>
                                )}
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Etiqueta</label>
                                    <input type="text" className="form-control" value={tramiteEdit.etiqueta || ''}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, etiqueta: e.target.value }))} />
                                </div>
                                <div className="d-flex gap-2">
                                    <button className="btn btn-primary rounded-pill px-4 fw-semibold" onClick={() => handleGuardarDatos()} disabled={guardandoDatos}>
                                        {guardandoDatos ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle me-2"></i>Guardar</>}
                                    </button>
                                    <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setEditMode(false)}>Cancelar</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="col-lg-6">
                    <div className="card shadow-lg border-0 rounded-4 p-4 h-100">
                        <h4 className="fw-bold mb-3" style={{ color: '#6c757d' }}>Información del Cliente</h4>
                        <p><strong>CUIL:</strong> {cliente.cuil || 'N/A'}</p>
                        <p><strong>Nombre:</strong> {cliente.nombre || 'N/A'}</p>
                        <p><strong>Apellido:</strong> {cliente.apellido || 'N/A'}</p>
                        <p><strong>Oficio / Actividad:</strong> {cliente.oficio || 'N/A'}</p>
                        <p><strong>Teléfono:</strong> {cliente.telefonoPropio || 'N/A'}</p>
                        {cliente.fechaNacimiento && (
                            <p><strong>Fecha de nacimiento:</strong> {new Date(cliente.fechaNacimiento).toLocaleDateString('es-ES')}</p>
                        )}
                        {cliente.cuil && (
                            <div className="mt-3 d-flex justify-content-center">
                                <button
                                    className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                    onClick={() => verDetalleCliente(cliente.cuil)}
                                >
                                    <PersonSearchIcon fontSize="medium" /> Ver Detalle del Cliente
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Expedientes de ANSES: acá, debajo de los datos del trámite y del cliente; arriba solo
                mientras el trámite está en "Seguimiento de Expediente". */}
            {!enSeguimientoDeExpediente(tramite.nombreEstadoActual) && <ExpedientesAnsesTramite idTramite={tramite.idTramite} />}

            {/* Panel destacado del cálculo — arriba de Documentación */}
            <PanelDestacadoReajuste tramite={tramite} onGuardarCalculo={handleGuardarCalculo} />

            {/* Ejecución / Cobro: visible mientras el trámite está en esa etapa, y se mantiene
                visible después si ya tiene datos cargados (no desaparece al cerrar el trámite). */}
            {mostrarPanelCobro && (
                <PanelCobroReajuste tramite={tramite} onGuardarCobro={handleGuardarCobro} />
            )}

            {/* Seguimiento del trámite: hitos cargados en cada avance de estado (patrón RTI/ART) */}
            {tieneHitos && (
                <div className="row g-4 mt-1">
                    <div className="col-12">
                        <div className="card shadow-lg border-0 rounded-4 p-4">
                            <h4 className="fw-bold text-primary mb-3"><i className="bi bi-signpost-split me-2"></i>Seguimiento del Trámite</h4>
                            <div className="row g-2">
                                {tramite.fechaLlamado && <div className="col-md-6"><strong>Llamado al cliente (aviso de turno):</strong> {formatearFechaCorta(tramite.fechaLlamado)}</div>}
                                {/* Dato de los reajustes cargados antes de que existiera el turno ANSES
                                    como hito propio. Se muestra para no perderlo; los nuevos van en la tarjeta. */}
                                {tramite.fechaTurnoAnses && <div className="col-md-6"><strong>Turno en ANSES (dato anterior):</strong> {tramite.fechaTurnoAnses}</div>}
                                {tramite.fechaPresentacionReclamo && <div className="col-md-6"><strong>Presentación del reclamo:</strong> {tramite.fechaPresentacionReclamo}</div>}
                                {tramite.fechaDenegatoria && <div className="col-md-6"><strong>Denegatoria de ANSES:</strong> {tramite.fechaDenegatoria}</div>}
                                {tramite.fechaInicioJuicio && <div className="col-md-6"><strong>Inicio de juicio:</strong> {tramite.fechaInicioJuicio}</div>}
                                {tramite.juzgado && <div className="col-md-6"><strong>Juzgado:</strong> {tramite.juzgado}</div>}
                                {tramite.caratula && <div className="col-md-6"><strong>Carátula:</strong> {tramite.caratula}</div>}
                                {tramite.nroExpedienteJudicial && <div className="col-md-6"><strong>N° de expediente judicial:</strong> {tramite.nroExpedienteJudicial}</div>}
                                {tramite.fechaSentencia && <div className="col-md-6"><strong>Fecha de sentencia:</strong> {tramite.fechaSentencia}</div>}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Documentación del Trámite: checklist de formularios + otros documentos (un solo lugar) */}
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <h4 className="fw-bold text-primary mb-3"><i className="bi bi-folder2-open me-2"></i>Documentación del Trámite</h4>

                        {/* Formularios requeridos */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="fw-bold text-secondary mb-0"><i className="bi bi-check2-square me-2"></i>Formularios requeridos</h6>
                            <span className={`badge ${claseBadgeContador(formularios.filter(f => f.presentado).length, formularios.length)}`}>{formularios.filter(f => f.presentado).length}/{formularios.length}</span>
                        </div>
                        {formularios.length === 0 ? (
                            <div className="alert alert-info border-0 rounded-3 text-center mb-0">
                                <p className="mb-0">No hay formularios en el checklist.</p>
                            </div>
                        ) : (
                            <ul className="list-group list-group-flush">
                                {formularios.map(f => (
                                    <li key={f.id} className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2 px-0">
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id={`chk-${f.id}`}
                                                checked={!!f.presentado} disabled={!!f.idArchivo}
                                                onChange={() => togglePresentado(f)} />
                                            <label className="form-check-label" htmlFor={`chk-${f.id}`}
                                                style={{ textDecoration: f.presentado ? 'line-through' : 'none', color: f.presentado ? '#6c757d' : 'inherit' }}>
                                                {f.nombre}
                                            </label>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            {f.idArchivo ? (
                                                <>
                                                    <button className="btn btn-sm btn-outline-primary rounded-pill"
                                                        onClick={() => descargarPorId(f.idArchivo)}
                                                        title={nombrePorId[f.idArchivo] || 'Descargar'}>
                                                        <i className="bi bi-download me-1"></i>
                                                        {nombrePorId[f.idArchivo] || 'Descargar'}
                                                    </button>
                                                    <button className="btn btn-sm btn-link text-danger p-0" title="Quitar adjunto"
                                                        onClick={() => quitarAdjunto(f)}>
                                                        <i className="bi bi-x-circle"></i>
                                                    </button>
                                                </>
                                            ) : (
                                                <label className="btn btn-sm btn-outline-secondary rounded-pill mb-0">
                                                    {subiendoItem === f.id
                                                        ? <span className="spinner-border spinner-border-sm" role="status"></span>
                                                        : <><i className="bi bi-paperclip me-1"></i>Adjuntar</>}
                                                    <input type="file" accept="application/pdf" hidden
                                                        onChange={(e) => adjuntarArchivo(f, e.target.files[0])} />
                                                </label>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <hr className="my-4" />

                        {/* Otros documentos (no vinculados a un formulario) */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="fw-bold text-secondary mb-0"><i className="bi bi-paperclip me-2"></i>Otros documentos</h6>
                            <label className="btn btn-sm btn-outline-primary rounded-pill mb-0">
                                {subiendoGeneral
                                    ? <span className="spinner-border spinner-border-sm" role="status"></span>
                                    : <><i className="bi bi-cloud-upload me-1"></i>Subir archivo</>}
                                <input type="file" hidden onChange={(e) => { subirGeneral(e.target.files[0]); e.target.value = ''; }} />
                            </label>
                        </div>
                        {archivosOtros.length === 0 ? (
                            <p className="text-muted fst-italic mb-0">No hay otros documentos cargados.</p>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {archivosOtros.map(a => (
                                    <div key={a.idArchivo} className="d-flex align-items-center justify-content-between border rounded-3 px-3 py-2">
                                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                            <i className="bi bi-file-earmark-text text-primary" style={{ fontSize: '1.25rem' }}></i>
                                            <div style={{ minWidth: 0 }}>
                                                <div className="fw-semibold text-truncate" style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                    onClick={() => descargarPorId(a.idArchivo)} title={a.nombre}>
                                                    {a.nombre}
                                                </div>
                                                <small className="text-muted">{formatTamano(a.tamaño)}</small>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                            <button className="btn btn-sm" style={{ color: '#0056b3', border: 'none' }} title="Descargar"
                                                onClick={() => descargarPorId(a.idArchivo)}>
                                                <i className="bi bi-download"></i>
                                            </button>
                                            <button className="btn btn-sm" style={{ color: '#dc3545', border: 'none' }} title="Eliminar"
                                                onClick={() => eliminarGeneral(a.idArchivo)}>
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Sección de Sentencia (visible desde el estado Sentencia en adelante) */}
            {mostrarSentencia && (
                <div className="row g-4 mt-1">
                    <div className="col-12">
                        <div className="card shadow-lg border-0 rounded-4 p-4">
                            <h4 className="fw-bold text-primary mb-3"><i className="bi bi-bank2 me-2"></i>Sentencia</h4>
                            <div className="row g-3">
                                <div className="col-lg-6">
                                    <SeccionInstanciaSentencia
                                        idTramite={idTramite}
                                        tipoInstancia="PRIMERA_INSTANCIA"
                                        titulo="1ª Instancia"
                                        dataInicial={sentenciaPrimeraInstancia}
                                        pautasDisponibles={pautasDisponibles}
                                        onGuardado={handleGuardadaSentencia}
                                        onPautaCreada={handlePautaCreada}
                                    />
                                </div>
                                <div className="col-lg-6">
                                    <SeccionInstanciaSentencia
                                        idTramite={idTramite}
                                        tipoInstancia="CAMARA"
                                        titulo="Cámara"
                                        dataInicial={sentenciaCamara}
                                        pautasDisponibles={pautasDisponibles}
                                        onGuardado={handleGuardadaSentencia}
                                        onPautaCreada={handlePautaCreada}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal crear evento */}
            <CrearEventoModal
                isOpen={mostrarModalEvento}
                onClose={() => setMostrarModalEvento(false)}
                onSave={handleGuardarEvento}
                selectedDate={fechaSeleccionadaEvento}
                idTramite={idTramite}
            />

            {/* Acciones irreversibles (cambiar tipo / eliminar definitivamente).
                El propio panel decide si mostrarse segun el rol. */}
            <PanelAccionesAvanzadasTramite
                idTramite={tramite?.idTramite}
                tipoTramite="REAJUSTE"
            />

            {/* Modal historial de estados (ámbito REAJUSTE) */}
            <HistorialTramite
                isOpen={modalHistorialAbierto}
                onClose={() => setModalHistorialAbierto(false)}
                idTramite={idTramite}
                onEstadoCambiado={recargarTramite}
                ambitoFiltro="REAJUSTE"
            />

            {/* Modal crear turno (reutiliza el turnero de Recepción, prellenado con el cliente) */}
            {mostrarModalTurno && (
                <div className={turneroStyles.overlay} onClick={() => setMostrarModalTurno(false)}>
                    <div className={turneroStyles.contenidoModal} onClick={(e) => e.stopPropagation()}>
                        <ModalCrearTurno
                            celdaSel={null}
                            valoresIniciales={{
                                nombre: cliente.nombre || '',
                                apellido: cliente.apellido || '',
                                cuil: cliente.cuil || '',
                                telefono: cliente.telefonoPropio || '',
                                tipoTramite: 'REAJUSTE',
                            }}
                            onCerrar={() => setMostrarModalTurno(false)}
                            onCreado={() => { setMostrarModalTurno(false); toast.success('Turno creado correctamente'); }}
                        />
                    </div>
                </div>
            )}

            <CustomModal {...modalState} onHide={hideModal} />
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
            <FeedbackButton tramite={tramite} />
        </div>
    );
}

export default VistaDetalladaTramiteReajuste;
